import React, { useEffect, useState } from 'react';
import apiService from '../services/api';

const ReviewsModal = ({ vendor, isOpen, onClose, onReviewAdded }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newReview, setNewReview] = useState({
    comment: '',
    reviewerName: '',
    reviewerEmail: '',
    agreeToTerms: false
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch reviews when modal opens
  useEffect(() => {
    if (isOpen && (vendor?._id || vendor?.id)) {
      fetchReviews();
    }
  }, [isOpen, vendor?._id, vendor?.id]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching reviews for vendor:', vendor.businessName, 'ID:', vendor._id);
      
      // Use the vendor ID (not slug) and correct API method
      const response = await apiService.getReviews(vendor._id || vendor.id);
      
      console.log('✅ Reviews API response:', response);
      
      if (response && response.success !== false) {
        const reviewsData = response.reviews || response.data || response || [];
        console.log('✅ Reviews fetched:', reviewsData);
        setReviews(reviewsData);
      } else {
        console.log('❌ Failed to fetch reviews:', response?.message || 'No reviews found');
        setReviews([]);
      }
    } catch (error) {
      console.error('❌ Error fetching reviews:', error);
      console.log('📝 Will show empty reviews list for now');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    // Validate form fields
    if (!newReview.comment.trim() || !newReview.reviewerName.trim() || !newReview.reviewerEmail.trim()) {
      alert('Please fill in all fields');
      return;
    }

    // Check minimum character requirement
    if (newReview.comment.trim().length < 5) {
      alert('Review must be at least 5 characters long');
      return;
    }

    // Check terms agreement
    if (!newReview.agreeToTerms) {
      alert('Please agree to the terms and conditions before submitting your review');
      return;
    }

    try {
      setSubmitting(true);
      console.log('📝 Submitting anonymous review for vendor:', vendor.slug);

      const reviewData = {
        vendor: vendor._id || vendor.id,  // Backend expects 'vendor' field with vendor ID
        comment: newReview.comment.trim(),
        reviewerName: newReview.reviewerName.trim()
        // reviewerEmail not needed for anonymous endpoint
      };

      console.log('📤 Anonymous review data:', reviewData);

      const response = await apiService.createReview(reviewData);
      
      if (response.success) {
        console.log('✅ Anonymous review submitted successfully:', response);
        
        // Add the new review to the list immediately with Anonymous name
        const newReviewObj = {
          _id: response.review?._id || Date.now(),
          comment: reviewData.comment,
          reviewerName: 'Anonymous', // Always show as Anonymous in display
          createdAt: new Date().toISOString(),
          ...response.review
        };
        
        setReviews(prevReviews => [newReviewObj, ...prevReviews]);
        
        // Reset form
        setNewReview({
          comment: '',
          reviewerName: '',
          reviewerEmail: '',
          agreeToTerms: false
        });
        
        // Trigger a callback to update the vendor's rating in the parent component
        if (onReviewAdded) {
          onReviewAdded(response.updatedVendor || vendor);
        }
        
        alert('Your anonymous review has been submitted successfully!');
      } else {
        alert(response.message || 'Failed to submit review');
      }
    } catch (error) {
      console.error('❌ Error submitting review:', error);
      alert('Error submitting review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };





  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              Reviews for {vendor?.businessName || vendor?.name}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Safe Harbor Disclaimer */}
          <div className="mb-6 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
            <p className="text-sm text-yellow-800">
              <strong>Disclaimer:</strong> Reviews are submitted by users and reflect their individual opinions and experiences. 
              These reviews do not represent the views of Ceremonzy. We do not verify the accuracy of user-submitted content 
              and are not responsible for its reliability or truthfulness.
            </p>
          </div>

          {/* Review Summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="text-lg font-bold text-maroon">
                {reviews.length} review{reviews.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Reviews List */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Customer Reviews</h3>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="text-gray-600">Loading reviews...</div>
              </div>
            ) : reviews.length > 0 ? (
              <div className="space-y-4">
                {reviews.map((review, index) => (
                  <div key={review._id || index} className="border-b pb-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-semibold text-gray-800">
                          Anonymous
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">
                            {new Date(review.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700">{review.comment}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600">
                No reviews yet. Be the first to leave a review!
              </div>
            )}
          </div>

          {/* Add Review Form */}
          <div className="border-t pt-6">
            <h3 className="text-xl font-semibold mb-4">Write a Review</h3>
            
            {/* Anonymous Notice */}
            <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Anonymous Reviews:</strong> All reviews will be displayed as "Anonymous" to protect your privacy. Your name and email are collected for verification purposes only and will not be shown publicly.
                  </p>
                </div>
              </div>
            </div>

            {/* Review Submission Disclaimer */}
            <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
              <p className="text-sm text-blue-800">
                <strong>Review Guidelines:</strong> Please provide honest, constructive feedback. 
                Your review will be public and should comply with our <a href="/terms-of-use" className="underline hover:text-blue-900" target="_blank">Terms of Use</a>. 
                We reserve the right to remove inappropriate content.
              </p>
            </div>

            <form onSubmit={handleSubmitReview} className="space-y-4">


              {/* Name and Email */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={newReview.reviewerName}
                    onChange={(e) => setNewReview(prev => ({ ...prev, reviewerName: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon focus:border-maroon"
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Email *
                  </label>
                  <input
                    type="email"
                    value={newReview.reviewerEmail}
                    onChange={(e) => setNewReview(prev => ({ ...prev, reviewerEmail: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon focus:border-maroon"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Review * (minimum 5 characters)
                </label>
                <textarea
                  value={newReview.comment}
                  onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                  rows={4}
                  minLength={5}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon focus:border-maroon"
                  placeholder="Share your experience... (minimum 5 characters)"
                  required
                />
              </div>

              {/* Terms and Conditions Agreement */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    checked={newReview.agreeToTerms}
                    onChange={(e) => setNewReview(prev => ({ ...prev, agreeToTerms: e.target.checked }))}
                    className="mt-1 h-4 w-4 text-maroon focus:ring-maroon border-gray-300 rounded"
                    required
                  />
                  <label htmlFor="agreeToTerms" className="text-sm text-gray-700">
                    <span className="font-medium">I agree to the terms and conditions *</span>
                    <div className="mt-1 text-xs text-gray-500">
                      By submitting this review, I confirm that:
                      <ul className="mt-1 ml-4 list-disc space-y-1">
                        <li>This review is based on my genuine experience with this vendor</li>
                        <li>I will not post false, misleading, or defamatory content</li>
                        <li>I understand this review will be publicly visible</li>
                        <li>I agree to Ceremonzy's <a href="/terms-of-use" target="_blank" className="text-maroon hover:underline">Terms of Use</a> and <a href="/privacy-policy" target="_blank" className="text-maroon hover:underline">Privacy Policy</a></li>
                      </ul>
                    </div>
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting || !newReview.agreeToTerms}
                className="w-full bg-maroon text-white py-3 px-6 rounded-lg hover:bg-red-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewsModal;